import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PoundSterling, Activity, Server, LayoutDashboard, Ticket } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { Skeleton } from '../components/ui/Skeleton';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { jiraJqlFromTemplate, jiraListUrl, jiraOrJql, openInNewTab } from '../lib/jira-links';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { isProductionEnvironment } from '../lib/production-environment';

export function Dashboard() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canViewInstanceReport } = usePermission('dashboard', 'instance_report');
  const { isAllowed: canViewCostsOnDashboard } = usePermission('dashboard', 'cloud_costs');
  const { isAllowed: canViewHealthStatus } = usePermission('dashboard', 'health_status');
  const latestFetchIdRef = useRef(0);
  
  // Only show NoGroupsMessage if user exists AND has no groups
  // Don't show for users who don't exist in the portal (user will be null)
  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  
  // If user exists but has no groups, show the message
  if (hasNoGroups) {
    return <NoGroupsMessage />;
  }
  const [loaded, setLoaded] = useState({
    costs: false,
    instanceReport: false,
    health: false,
    jiraNext: false,
    jiraLastTouched: false,
    jiraPrioritySummary: false,
    alertsSummary: false,
  });
  const [stats, setStats] = useState({
    costMonthToDate: 0,
    costProjectedMonthEnd: 0,
    costsByProvider: { hetzner: 0, aws: 0, oci: 0 } as { hetzner: number; aws: number; oci: number },
    health: 'Unknown' as 'Healthy' | 'Disrupted' | 'Unknown',
    alarms: 0,
    alerts: 0,
  });
  const [jiraNext, setJiraNext] = useState<any>(null);
  const [jiraLastTouched, setJiraLastTouched] = useState<any>(null);
  const [jiraPrioritySummary, setJiraPrioritySummary] = useState<any>(null);
  const [alertsSummary, setAlertsSummary] = useState<any>(null);
  const [instanceReport, setInstanceReport] = useState<Record<string, { oci: number; hetzner: number; aws: number }>>({});
  const [tenantNameByCode, setTenantNameByCode] = useState<Map<string, string>>(new Map());
  const [alertsDialog, setAlertsDialog] = useState<{
    open: boolean;
    loading: boolean;
    title: string;
    incidents: any[];
  }>({ open: false, loading: false, title: '', incidents: [] });

  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;
  const tenantsKey = useMemo(
    () => tenantsToQuery.map((t) => t.toLowerCase()).join(','),
    [tenantsToQuery.join(',')]
  );
  const selectedTenantSet = useMemo(() => new Set(tenantsToQuery.map(t => t.toLowerCase())), [tenantsToQuery.join(',')]);

  // If user can view costs and is on dashboard, they are a costs cache subscriber
  useActivityHeartbeat('cache:costs', !!user && canViewCostsOnDashboard);
  // If user can view instance report on dashboard, they are a tenant_servers cache subscriber
  useActivityHeartbeat('cache:tenant_servers', !!user && canViewInstanceReport);
  // Keep Jira cache warm for ticket summary cards shown on dashboard.
  useActivityHeartbeat('cache:service_health', !!user && canViewHealthStatus);

  // When tenant selection changes, immediately flip cards back to skeletons (before paint)
  // so we never show "Unknown/0" placeholders for the new selection.
  useLayoutEffect(() => {
    setLoaded({
      costs: !canViewCostsOnDashboard,
      instanceReport: !canViewInstanceReport,
      health: !canViewHealthStatus,
      jiraNext: !canViewHealthStatus,
      jiraLastTouched: !canViewHealthStatus,
      jiraPrioritySummary: !canViewHealthStatus,
      alertsSummary: !canViewHealthStatus,
    });
    // Also clear any previous-tenant values that drive visual indicators (e.g. pulse animations)
    // so skeleton states never show stale "bad" status for the new tenant selection.
    if (canViewHealthStatus) {
      setStats((prev) => ({ ...prev, health: 'Unknown', alarms: 0, alerts: 0 }));
      setAlertsSummary(null);
    }
  }, [tenantsKey, canViewCostsOnDashboard, canViewInstanceReport, canViewHealthStatus]);

  type FetchScope = { costs?: boolean; instanceReport?: boolean; jira?: boolean; healthAlerts?: boolean };

  const fetchStats = useCallback(async (isBackgroundRefresh: boolean = false, scope?: FetchScope) => {
    const fetchId = ++latestFetchIdRef.current;
    const s: Required<FetchScope> = {
      costs: scope?.costs ?? true,
      instanceReport: scope?.instanceReport ?? true,
      jira: scope?.jira ?? true,
      healthAlerts: scope?.healthAlerts ?? true,
    };
    try {
      // Foreground loads should show skeletons until values are available.
      // Background refreshes should keep the current values on screen.
      if (!isBackgroundRefresh && fetchId === latestFetchIdRef.current) {
        setLoaded({
          costs: !canViewCostsOnDashboard,
          instanceReport: !canViewInstanceReport,
          health: !canViewHealthStatus,
          jiraNext: !canViewHealthStatus,
          jiraLastTouched: !canViewHealthStatus,
          jiraPrioritySummary: !canViewHealthStatus,
          alertsSummary: !canViewHealthStatus,
        });
      }

      const tenantsParam = encodeURIComponent(tenantsKey);
      const tenantsCsv = tenantsKey;
      const isCurrentFetch = () => fetchId === latestFetchIdRef.current;

      const filterInstanceReport = (
        tenants: Record<string, { oci: number; hetzner: number; aws: number }> | undefined,
      ) => {
        const filtered: Record<string, { oci: number; hetzner: number; aws: number }> = {};
        for (const [tenant, counts] of Object.entries(tenants || {})) {
          if (selectedTenantSet.has(tenant.toLowerCase())) {
            filtered[tenant] = counts;
          }
        }
        return filtered;
      };

      const tasks: Promise<void>[] = [];

      if (s.costs && canViewCostsOnDashboard) {
        tasks.push(
          apiClient
            .get<{
              costMonthToDate: number;
              costProjectedMonthEnd: number;
              costsByProvider?: { hetzner: number; aws: number; oci: number };
              requestedTenants: number;
              cachedTenants: number;
              ready: boolean;
            }>(`/api/dashboard/cost-summary?tenants=${tenantsParam}`)
            .then((value) => {
              if (!isCurrentFetch()) return;
              if (!value.ready) return;
              setStats((prev) => ({
                ...prev,
                costMonthToDate: value.costMonthToDate,
                costProjectedMonthEnd: value.costProjectedMonthEnd,
                costsByProvider: value.costsByProvider || { hetzner: 0, aws: 0, oci: 0 },
              }));
              setLoaded((prev) => ({ ...prev, costs: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );
      }

      if (s.instanceReport && canViewInstanceReport) {
        tasks.push(
          apiClient
            .get<{
              tenants: Record<string, { oci: number; hetzner: number; aws: number }>;
              requestedTenants: number;
              cachedTenants: number;
              ready: boolean;
            }>(`/api/dashboard/instance-report?tenants=${tenantsParam}`)
            .then((value) => {
              if (!isCurrentFetch()) return;
              setInstanceReport(filterInstanceReport(value.tenants));
              setLoaded((prev) => ({ ...prev, instanceReport: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );
      }

      if (s.healthAlerts && canViewHealthStatus) {
        tasks.push(
          apiClient
            .getDashboardHealth(tenantsCsv)
            .then((health: any) => {
              if (!isCurrentFetch()) return;
              setStats((prev) => ({
                ...prev,
                health: (health.status as any) || 'Unknown',
                alarms: health.alarms ?? 0,
                alerts: health.alerts ?? 0,
              }));
              setLoaded((prev) => ({ ...prev, health: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );

        tasks.push(
          apiClient
            .getDashboardAlertsSummary(tenantsCsv)
            .then((v: any) => {
              if (!isCurrentFetch()) return;
              setAlertsSummary(v);
              setLoaded((prev) => ({ ...prev, alertsSummary: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );
      }

      if (s.jira && canViewHealthStatus) {
        tasks.push(
          apiClient
            .getDashboardJiraNext(tenantsCsv)
            .then((v: any) => {
              if (!isCurrentFetch()) return;
              setJiraNext(v);
              setLoaded((prev) => ({ ...prev, jiraNext: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );

        tasks.push(
          apiClient
            .getDashboardJiraLastTouched(tenantsCsv)
            .then((v: any) => {
              if (!isCurrentFetch()) return;
              setJiraLastTouched(v);
              setLoaded((prev) => ({ ...prev, jiraLastTouched: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );

        tasks.push(
          apiClient
            .getDashboardJiraPrioritySummary(tenantsCsv)
            .then((v: any) => {
              if (!isCurrentFetch()) return;
              setJiraPrioritySummary(v);
              setLoaded((prev) => ({ ...prev, jiraPrioritySummary: true }));
            })
            .catch(() => {
              /* keep skeleton */
            }),
        );
      }

      await Promise.allSettled(tasks);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [tenantsKey, canViewCostsOnDashboard, canViewInstanceReport, canViewHealthStatus, selectedTenantSet]);

  useEffect(() => {
    void fetchStats(false);
  }, [fetchStats]);

  // Live refresh when costs cache updates (no full reload)
  useCacheEvents({
    enabled: !!user && canViewCostsOnDashboard,
    cacheNames: ['costs'],
    onEvent: () => void fetchStats(true, { costs: true, instanceReport: false, jira: false, healthAlerts: false }),
  });
  useCacheEvents({
    enabled: !!user && canViewInstanceReport,
    cacheNames: ['tenant_servers'],
    onEvent: () => void fetchStats(true, { costs: false, instanceReport: true, jira: false, healthAlerts: false }),
  });

  // Refresh Jira cards when Jira cache updates
  useCacheEvents({
    enabled: !!user && canViewHealthStatus,
    cacheNames: ['jira'],
    onEvent: () => void fetchStats(true, { costs: false, instanceReport: false, jira: true, healthAlerts: false }),
  });

  // Refresh Health + Alerts cards when incident store reports semantic changes.
  // IMPORTANT: do NOT subscribe directly to raw source caches like `uptime` because they may refresh every minute
  // (e.g. new probe samples) even when the derived incident state did not change.
  useCacheEvents({
    enabled: !!user && canViewHealthStatus,
    cacheNames: ['cache:alerts', 'cache:service_health'],
    onEvent: () => void fetchStats(true, { costs: false, instanceReport: false, jira: false, healthAlerts: true }),
  });

  const isNotHealthy = loaded.health && stats.health === 'Disrupted';
  const healthIconColor =
    !loaded.health
      ? 'text-gray-400'
      : stats.health === 'Healthy'
        ? 'text-green-500'
        : stats.health === 'Disrupted'
          ? 'text-red-500'
          : 'text-gray-400';
  const showHealthSkeleton = canViewHealthStatus && !loaded.health;
  const showJiraPrioritySkeleton = canViewHealthStatus && !loaded.jiraPrioritySummary;
  const showAlertsSkeleton = canViewHealthStatus && !loaded.alertsSummary;
  const showCostsSkeleton = canViewCostsOnDashboard && !loaded.costs;
  const showJiraNextSkeleton = canViewHealthStatus && !loaded.jiraNext;
  const showJiraLastTouchedSkeleton = canViewHealthStatus && !loaded.jiraLastTouched;
  const showInstanceReportSkeleton = canViewInstanceReport && !loaded.instanceReport;

  const ensureTenantNamesLoaded = useCallback(async () => {
    if (tenantNameByCode.size > 0) return;
    try {
      const tenantsResponse = await apiClient.getTenants();
      const tenantsArr = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const map = new Map<string, string>();
      for (const t of tenantsArr) {
        const code = String((t as any)?.code || '').trim().toLowerCase();
        const name = String((t as any)?.name || '').trim();
        if (code) map.set(code, name || code.toUpperCase());
      }
      setTenantNameByCode(map);
    } catch {
      // best-effort: fall back to codes
      setTenantNameByCode(new Map());
    }
  }, [tenantNameByCode.size]);

  const openAlertsDialogFor = useCallback(async (args: { show: 'alarms' | 'alerts'; source?: string }) => {
    const show = args.show;
    const source = String(args.source || 'any').toLowerCase();

    setAlertsDialog({ open: true, loading: true, title: '', incidents: [] });
    await ensureTenantNamesLoaded();

    try {
      const tenantCodes = (tenantsToQuery || []).map((t) => String(t || '').trim().toLowerCase()).filter(Boolean);
      const results = await Promise.allSettled(tenantCodes.map((tc) => apiClient.getMonitoringCurrent(tc)));

      const incidents: any[] = [];
      for (let idx = 0; idx < results.length; idx += 1) {
        const tc = tenantCodes[idx];
        const r = results[idx];
        if (!tc || r.status !== 'fulfilled') continue;
        const cur = r.value?.current || {};
        const list = show === 'alarms' ? (cur.alarms || []) : (cur.alerts || []);
        for (const i of list) {
          const s = String(i?.source || '').toLowerCase();
          if (source !== 'any' && s !== source) continue;
          if (!isProductionEnvironment(i?.environment)) continue;
          incidents.push({ ...i, tenantCode: i?.tenantCode || tc });
        }
      }

      const srcLabel = source !== 'any'
        ? `${source.charAt(0).toUpperCase()}${source.slice(1)}`
        : null;
      const title = `${show === 'alarms' ? 'Alarms' : 'Alerts'}${srcLabel ? ` - ${srcLabel}` : ''}`;

      setAlertsDialog({ open: true, loading: false, title, incidents });
    } catch {
      setAlertsDialog({ open: true, loading: false, title: 'Incidents', incidents: [] });
    }
  }, [ensureTenantNamesLoaded, tenantsToQuery.join(',')]);

  const renderDashboardIncident = useCallback((i: any, idx: number) => {
    const sev = String(i?.severity || '').toLowerCase();
    const isAlarm = sev === 'alarm';
    const borderColor = isAlarm ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-orange-500 bg-orange-50 dark:bg-orange-900/10';
    const textColor = isAlarm ? 'text-red-700 dark:text-red-300' : 'text-orange-700 dark:text-orange-300';

    const tc = String(i?.tenantCode || '').trim().toLowerCase();
    const tenantLabel = (tc && tenantNameByCode.get(tc)) ? String(tenantNameByCode.get(tc)) : (tc ? tc.toUpperCase() : '—');
    const env = String(i?.environment || '').trim();
    const src = String(i?.source || '').trim();
    const startedAt = i?.startedAt ? new Date(i.startedAt).toLocaleString() : '—';

    const uniqueKey = `${tc || 'no-tenant'}-${env || 'noenv'}-${String(i?.monitorId || i?.serviceId || 'x')}-${String(i?.startedAt || '')}-${idx}`;

    return (
      <div key={uniqueKey} className={`rounded-lg border-l-4 ${borderColor} p-3`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className={`text-sm font-bold ${textColor} uppercase`}>{isAlarm ? 'alarm' : 'alert'}</div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {tenantLabel && <Badge variant="outline" className="text-xs">{tenantLabel}</Badge>}
            {env && <Badge variant="outline" className="text-xs">{env}</Badge>}
            {src && <Badge variant="outline" className="text-xs">{src.charAt(0).toUpperCase() + src.slice(1)}</Badge>}
          </div>
        </div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{String(i?.title || '').trim() || '—'}</div>
        {i?.description && (
          <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 italic">
            {String(i.description)}
          </div>
        )}
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          <span>{String(i?.serviceName || i?.serviceId || '').trim() || '—'}</span>
          <span className="mx-1">·</span>
          <span>since {startedAt}</span>
        </div>
      </div>
    );
  }, [tenantNameByCode]);

  const openJiraListFor = useCallback((args: { templateKey: string; projectKeys: string[]; title?: string }) => {
    const cfg = jiraPrioritySummary?.jiraLinkConfig;
    const baseUrl = String(cfg?.baseUrl || '');
    const jql = jiraJqlFromTemplate({
      jiraLinkConfig: cfg,
      templateKey: args.templateKey,
      projectKeys: args.projectKeys,
      stripOrderBy: true,
    });
    const url = jql ? jiraListUrl(baseUrl, jql) : null;
    openInNewTab(url);
  }, [jiraPrioritySummary]);

  const openJiraListForTotal = useCallback((args: { templateKeys: string[]; projectKeys: string[] }) => {
    const cfg = jiraPrioritySummary?.jiraLinkConfig;
    const baseUrl = String(cfg?.baseUrl || '');
    const jqls = args.templateKeys.map((k) => jiraJqlFromTemplate({
      jiraLinkConfig: cfg,
      templateKey: k,
      projectKeys: args.projectKeys,
      stripOrderBy: true,
    }));
    const jql = jiraOrJql(jqls);
    const url = jql ? jiraListUrl(baseUrl, jql) : null;
    openInNewTab(url);
  }, [jiraPrioritySummary]);

  return (
    <div className="space-y-4">
      <PageHeader
        icon={LayoutDashboard}
        description="Overview across your selected tenants"
        count={tenantsToQuery.length}
        countLabel="tenants"
      />

      {/* Summary */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2 lg:grid-cols-3 min-w-0">
        {canViewHealthStatus && (
          <Card
            className="min-w-0 flex flex-col"
            style={isNotHealthy ? { animation: 'pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <CardTitle className="text-sm font-medium">Cloud Health Status</CardTitle>
              <Activity className={`h-4 w-4 ${healthIconColor}`} />
            </CardHeader>
            <CardContent className="p-4 pt-0 flex-1 flex flex-col items-center justify-center text-center">
              <div className={`text-4xl md:text-5xl font-bold leading-none ${
                stats.health === 'Healthy'
                  ? 'text-green-600 dark:text-green-400'
                  : stats.health === 'Disrupted'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}>
                {showHealthSkeleton ? <Skeleton className="h-9 w-28" /> : stats.health}
              </div>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                {showHealthSkeleton ? (
                  <Skeleton className="h-3 w-24 inline-block" />
                ) : (
                  <span>{stats.alarms} alarms · {stats.alerts} alerts</span>
                )}
              </p>
            </CardContent>
          </Card>
        )}

        {canViewHealthStatus && (
          <Card className="min-w-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <CardTitle className="text-sm font-medium">Ticket priority summary</CardTitle>
              <Ticket className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {showJiraPrioritySkeleton ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center">
                      {(() => {
                        const v = Number(jiraPrioritySummary?.openOrWip?.total ?? 0) || 0;
                        const pks = Array.isArray(jiraPrioritySummary?.projectKeys) ? jiraPrioritySummary.projectKeys : [];
                        const enabled = v > 0 && pks.length > 0 && !!jiraPrioritySummary?.jiraLinkConfig?.baseUrl;
                        return (
                          <button
                            type="button"
                            onClick={() => openJiraListForTotal({ templateKeys: ['jiraP2OpenOrWip', 'jiraP3OpenOrWip', 'jiraP4OpenOrWip'], projectKeys: pks })}
                            disabled={!enabled}
                            className={`text-2xl font-bold tabular-nums leading-none ${enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                            title="Open ticket list"
                          >
                            {v}
                          </button>
                        );
                      })()}
                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Open / WIP</div>
                    </div>
                    <div className="text-center">
                      {(() => {
                        const v = Number(jiraPrioritySummary?.unresolved?.total ?? 0) || 0;
                        const pks = Array.isArray(jiraPrioritySummary?.projectKeys) ? jiraPrioritySummary.projectKeys : [];
                        const enabled = v > 0 && pks.length > 0 && !!jiraPrioritySummary?.jiraLinkConfig?.baseUrl;
                        return (
                          <button
                            type="button"
                            onClick={() => openJiraListFor({ templateKey: 'backlogTotal', projectKeys: pks })}
                            disabled={!enabled}
                            className={`text-2xl font-bold tabular-nums leading-none ${enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}
                            title="Open ticket list"
                          >
                            {v}
                          </button>
                        );
                      })()}
                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Unresolved</div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">BY PRIORITY</div>
                    <div className="mt-1 grid grid-cols-4 gap-2">
                      {([
                        { key: 'p1', label: 'P1', textOpen: 'text-red-600 dark:text-red-400', textUnres: 'text-red-600 dark:text-red-400' },
                        { key: 'p2', label: 'P2', textOpen: 'text-orange-600 dark:text-orange-400', textUnres: 'text-orange-600 dark:text-orange-400' },
                        { key: 'p3', label: 'P3', textOpen: 'text-yellow-700 dark:text-yellow-400', textUnres: 'text-yellow-700 dark:text-yellow-400' },
                        { key: 'p4', label: 'P4', textOpen: 'text-blue-600 dark:text-blue-400', textUnres: 'text-blue-600 dark:text-blue-400' },
                      ] as const).map((p) => {
                        const openV = Number(jiraPrioritySummary?.openOrWip?.[p.key] ?? 0) || 0;
                        const unresV = Number(jiraPrioritySummary?.unresolved?.[p.key] ?? 0) || 0;
                        const hasAny = openV > 0 || unresV > 0;
                        const pks = Array.isArray(jiraPrioritySummary?.projectKeys) ? jiraPrioritySummary.projectKeys : [];
                        const hasCfg = pks.length > 0 && !!jiraPrioritySummary?.jiraLinkConfig?.baseUrl;
                        const openTemplateKey = p.key === 'p2' ? 'jiraP2OpenOrWip' : p.key === 'p3' ? 'jiraP3OpenOrWip' : p.key === 'p4' ? 'jiraP4OpenOrWip' : null;
                        const unresTemplateKey = p.key === 'p1' ? 'backlogP1' : p.key === 'p2' ? 'backlogP2' : p.key === 'p3' ? 'backlogP3' : 'backlogP4';
                        return (
                          <div key={`prio-${p.key}`} className="text-center">
                            <div className="text-[10px] text-gray-600 dark:text-gray-400 leading-none">{p.label}</div>
                            <div className="mt-1 flex items-baseline justify-center gap-2">
                              {openTemplateKey ? (
                                <button
                                  type="button"
                                  onClick={() => openJiraListFor({ templateKey: openTemplateKey, projectKeys: pks })}
                                  disabled={!(hasCfg && openV > 0)}
                                  className={`text-xl font-bold tabular-nums leading-none ${hasCfg && openV > 0 ? p.textOpen : 'text-gray-400'}`}
                                  title="Open ticket list"
                                >
                                  {openV}
                                </button>
                              ) : (
                                <span className={`text-xl font-bold tabular-nums leading-none ${hasAny ? p.textOpen : 'text-gray-400'}`}>{openV}</span>
                              )}
                              <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                              <button
                                type="button"
                                onClick={() => openJiraListFor({ templateKey: unresTemplateKey, projectKeys: pks })}
                                disabled={!(hasCfg && unresV > 0)}
                                className={`text-xl font-bold tabular-nums leading-none ${hasCfg && unresV > 0 ? p.textUnres : 'text-gray-400'}`}
                                title="Open ticket list"
                              >
                                {unresV}
                              </button>
                            </div>
                            <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-500 leading-none">open & wip / unresolved</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {canViewHealthStatus && (
          <Card
            className="min-w-0"
            style={loaded.alertsSummary && (Number(alertsSummary?.total?.alarms ?? 0) || 0) > 0 ? { animation: 'pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <CardTitle className="text-sm font-medium">Alerts & Alarms</CardTitle>
              <Activity className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {showAlertsSkeleton ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <div className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-center">
                      {(() => {
                        const v = Number(alertsSummary?.total?.alarms ?? 0) || 0;
                        return (
                          <button
                            type="button"
                            onClick={() => void openAlertsDialogFor({ show: 'alarms', source: 'any' })}
                            disabled={v === 0}
                            className={`text-2xl font-bold tabular-nums leading-none ${v > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                            title="Open list"
                          >
                            {v}
                          </button>
                        );
                      })()}
                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alarms</div>
                    </div>
                    <div className="text-center">
                      {(() => {
                        const v = Number(alertsSummary?.total?.alerts ?? 0) || 0;
                        return (
                          <button
                            type="button"
                            onClick={() => void openAlertsDialogFor({ show: 'alerts', source: 'any' })}
                            disabled={v === 0}
                            className={`text-2xl font-bold tabular-nums leading-none ${v > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                            title="Open list"
                          >
                            {v}
                          </button>
                        );
                      })()}
                      <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 leading-none">Alerts</div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">By Source</div>
                    <div className="grid grid-cols-3 gap-2">
                      {(['uptime', 'grafana', 'alertmanager'] as const).map((src) => {
                        const s = alertsSummary?.sources?.[src] || {};
                        const alarms = Number(s.alarms ?? 0) || 0;
                        const alerts = Number(s.alerts ?? 0) || 0;
                        return (
                          <div key={src} className="text-center">
                            <div className="mt-1 text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase">{src}</div>
                            <div className="mt-1 flex items-baseline justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void openAlertsDialogFor({ show: 'alarms', source: src })}
                                disabled={alarms === 0}
                                className={`text-xl font-bold tabular-nums leading-none ${alarms > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}
                                title="Open list"
                              >
                                {alarms}
                              </button>
                              <span className="text-[10px] text-gray-500 dark:text-gray-500 leading-none">/</span>
                              <button
                                type="button"
                                onClick={() => void openAlertsDialogFor({ show: 'alerts', source: src })}
                                disabled={alerts === 0}
                                className={`text-xl font-bold tabular-nums leading-none ${alerts > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}
                                title="Open list"
                              >
                                {alerts}
                              </button>
                            </div>
                            <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-500 leading-none">alarms / alerts</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {canViewCostsOnDashboard && (
          <Card className="min-w-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <CardTitle className="text-sm font-medium">Cloud Costs (Selected Tenants)</CardTitle>
              <PoundSterling className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {showCostsSkeleton ? <Skeleton className="h-8 w-20" /> : `£${stats.costMonthToDate.toFixed(2)}`}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Projected month end:{' '}
                {showCostsSkeleton ? <Skeleton className="h-3 w-16 inline-block" /> : `£${stats.costProjectedMonthEnd.toFixed(2)}`}
              </p>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                {showCostsSkeleton ? (
                  <Skeleton className="h-3 w-44" />
                ) : (() => {
                  const total = (Number(stats.costsByProvider.hetzner) || 0) + (Number(stats.costsByProvider.aws) || 0) + (Number(stats.costsByProvider.oci) || 0);
                  const pct = (v: number) => (total > 0 ? `${Math.round((v / total) * 100)}%` : '—');
                  return (
                    <span className="tabular-nums">
                      Hetzner {pct(Number(stats.costsByProvider.hetzner) || 0)} · AWS {pct(Number(stats.costsByProvider.aws) || 0)} · OCI {pct(Number(stats.costsByProvider.oci) || 0)}
                    </span>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {canViewHealthStatus && (
          <Card className="min-w-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <CardTitle className="text-sm font-medium">Next ticket to attend</CardTitle>
              <Ticket className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {showJiraNextSkeleton ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-3 w-56" />
                </div>
              ) : (jiraNext?.ticket ? (
                <button
                  type="button"
                  onClick={() => openInNewTab(jiraNext?.ticket?.url || null)}
                  className="space-y-1.5 text-left w-full"
                  title="Open ticket"
                >
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {jiraNext.ticket.key} · {jiraNext.ticket.priority || '—'}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {jiraNext.ticket.status} · {jiraNext.ticket.timeToResolutionRemainingHours === null || jiraNext.ticket.timeToResolutionRemainingHours === undefined
                      ? 'SLA: —'
                      : `SLA: ${Number(jiraNext.ticket.timeToResolutionRemainingHours).toFixed(1)}h`}
                    {jiraNext.ticket.touchedAt ? ` · ${new Date(jiraNext.ticket.touchedAt).toLocaleString()}` : ''}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 break-words line-clamp-2">
                    {jiraNext.ticket.summary || ''}
                  </div>
                </button>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">No open / WIP incidents</div>
              ))}
            </CardContent>
          </Card>
        )}

        {canViewHealthStatus && (
          <Card className="min-w-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <CardTitle className="text-sm font-medium">Last ticket touched by customer</CardTitle>
              <Ticket className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {showJiraLastTouchedSkeleton ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-3 w-56" />
                </div>
              ) : (jiraLastTouched?.ticket ? (
                <button
                  type="button"
                  onClick={() => openInNewTab(jiraLastTouched?.ticket?.url || null)}
                  className="space-y-1.5 text-left w-full"
                  title="Open ticket"
                >
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {jiraLastTouched.ticket.key} · {jiraLastTouched.ticket.priority || '—'}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {jiraLastTouched.ticket.status} · {jiraLastTouched.ticket.timeToResolutionRemainingHours === null || jiraLastTouched.ticket.timeToResolutionRemainingHours === undefined
                      ? 'SLA: —'
                      : `SLA: ${Number(jiraLastTouched.ticket.timeToResolutionRemainingHours).toFixed(1)}h`}
                    {jiraLastTouched.ticket.touchedAt ? ` · ${new Date(jiraLastTouched.ticket.touchedAt).toLocaleString()}` : ''}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 break-words line-clamp-2">
                    {jiraLastTouched.ticket.summary || ''}
                  </div>
                </button>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">No open / WIP incidents</div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Instance Report */}
      {canViewInstanceReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              Instance Report
            </CardTitle>
            <CardDescription>Instances per cloud provider for selected tenants</CardDescription>
          </CardHeader>
          <CardContent>
            {showInstanceReportSkeleton ? (
              <Skeleton className="h-28 w-full" />
            ) : Object.keys(instanceReport).length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">No data</div>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const maxTotal = Math.max(
                    1,
                    ...Object.values(instanceReport).map((c) => (c.oci || 0) + (c.hetzner || 0) + (c.aws || 0))
                  );

                  return Object.entries(instanceReport).map(([tenant, counts]) => {
                  const total = (counts.oci || 0) + (counts.hetzner || 0) + (counts.aws || 0);
                  return (
                    <div key={tenant} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="font-medium">{tenant.toUpperCase()}</div>
                        <div className="text-gray-500 dark:text-gray-400">{total} total</div>
                      </div>
                      <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded overflow-hidden flex">
                        <div className="bg-red-500 h-full" style={{ width: `${((counts.oci || 0) / maxTotal) * 100}%` }} />
                        <div className="bg-red-600 h-full" style={{ width: `${((counts.hetzner || 0) / maxTotal) * 100}%` }} />
                        <div className="bg-orange-500 h-full" style={{ width: `${((counts.aws || 0) / maxTotal) * 100}%` }} />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-3">
                        <span>OCI: {counts.oci}</span>
                        <span>Hetzner: {counts.hetzner}</span>
                        <span>AWS: {counts.aws}</span>
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <style>{`
        @keyframes pulse-red {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(239, 68, 68, 0.1); }
        }
        @media (prefers-color-scheme: dark) {
          @keyframes pulse-red {
            0%, 100% { background-color: transparent; }
            50% { background-color: rgba(239, 68, 68, 0.2); }
          }
        }
      `}</style>

      {/* Dashboard Alerts & Alarms list dialog */}
      {alertsDialog.open && (
        <Dialog open={alertsDialog.open} onOpenChange={(open) => setAlertsDialog((prev) => ({ ...prev, open }))}>
          <DialogContent maxWidth="2xl" className="flex flex-col max-h-[90vh]">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{alertsDialog.title || 'Incidents'}</DialogTitle>
            </DialogHeader>
            <DialogBody className="flex-1 overflow-y-auto">
              {alertsDialog.loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : alertsDialog.incidents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">No incidents</div>
              ) : (
                <div className="space-y-2">
                  {alertsDialog.incidents.map((i, idx) => renderDashboardIncident(i, idx))}
                </div>
              )}
            </DialogBody>
            <DialogFooter className="flex-shrink-0">
              <Button onClick={() => setAlertsDialog((prev) => ({ ...prev, open: false }))}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

